import { AnalyticsEventNameType, FeatureFlag } from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useWebDashboardOffer } from "../../api/users";
import { CoefficientIcon } from "../../components/Icons/logos";
import { AppState } from "../../store";
import { useTrack } from "../../utils";
import {
  submitWebDashboardExpiredSalesForm,
  submitWebDashboardProactiveSalesForm,
} from "../Billing/billing.utils";
import {
  WEB_DASHBOARD_UPGRADE_MODAL_SOURCES,
  WebDashboardUpgradeModalVariant,
  getAddAiDashboardsUrl,
  getPlanChooserUrl,
  getWebDashboardUpgradeDestination,
} from "../Billing/planChooserSource.utils";

interface WebDashboardUpgradeModalProps {
  onClose: () => void;
  variant?: WebDashboardUpgradeModalVariant;
  dashboardId?: string;
}

type ModalCopy = Record<
  NonNullable<WebDashboardUpgradeModalProps["variant"]>,
  { title: string; body: React.ReactNode }
>;

const MODAL_COPY: ModalCopy = {
  expired: {
    title: "Upgrade plan",
    body: "Upgrade your plan to continue creating, editing and refreshing dashboards.",
  },
  proactive: {
    title: "Upgrade plan",
    body: "Upgrade your plan to continue creating, editing and refreshing dashboards.",
  },
  trial_info: {
    title: "AI Dashboards Trial",
    body: (
      <>
        Dashboards is a new add-on to Coefficient plans.
        <br />
        <br />
        You have unlimited AI dashboard access for 30 days during your free trial. To learn more
        about pricing, contact sales.
      </>
    ),
  },
};

// ENG-9208: with self-serve on, the copy points to plans and names only what an expired trial
// actually blocks (AI chat, create, duplicate and refresh; hand edits in the viewer are not gated).
const SELF_SERVE_UPGRADE_BODY =
  "Upgrade your plan to keep creating dashboards, editing them with AI, and refreshing their data.";
const SELF_SERVE_MODAL_COPY: ModalCopy = {
  expired: { title: "Upgrade plan", body: SELF_SERVE_UPGRADE_BODY },
  proactive: { title: "Upgrade plan", body: SELF_SERVE_UPGRADE_BODY },
  trial_info: {
    title: "AI Dashboards Trial",
    body: (
      <>
        Dashboards is a new add-on to Coefficient plans.
        <br />
        <br />
        You have unlimited AI dashboard access for 30 days during your free trial. To keep using AI
        Dashboards after your trial, view plans.
      </>
    ),
  },
};

export const WebDashboardUpgradeModal: React.FC<WebDashboardUpgradeModalProps> = ({
  onClose,
  variant = "expired",
  dashboardId,
}) => {
  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);
  const track = useTrack();

  // With self-serve AI Dashboards, the primary CTA routes to the billing page instead of the
  // contact-sales form (which stays available as a secondary action).
  const { value: enableSelfServeAiDashboards } = useFeatureGate(
    FeatureFlag.EnableSelfServeAiDashboards
  );

  // Where View plans goes depends on the user's self-serve offer: eligible Pro subscribers get
  // the confirm modal on their subscription page, everyone else the plan chooser (ineligible
  // users get contact sales there).
  const { data: offer, isLoading: isOfferLoading } = useWebDashboardOffer({
    enabled: enableSelfServeAiDashboards,
  });
  const destination = getWebDashboardUpgradeDestination(offer?.web_dashboard_eligibility);
  const showViewPlans = enableSelfServeAiDashboards;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [isRequestSent, setIsRequestSent] = useState(false);

  const copy = (enableSelfServeAiDashboards ? SELF_SERVE_MODAL_COPY : MODAL_COPY)[variant];

  // ENG-9207: the plan chooser's `?from=` naming this modal variant. ENG-9205: the modal's
  // events carry it too, so they join with the billing events on the page View plans opens.
  const planChooserFrom = WEB_DASHBOARD_UPGRADE_MODAL_SOURCES[variant];
  const eventProps = {
    vibe_dash_id: dashboardId,
    vibe_dash_upgrade_modal_variant: variant,
    event_from: planChooserFrom,
  };

  useEffect(() => {
    track(AnalyticsEventNameType.VibeDashUpgradeModalViewed, eventProps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewPlans = () => {
    track(AnalyticsEventNameType.VibeDashUpgradeModalViewPlansClicked, eventProps);
    window.open(
      destination === "add_on_modal"
        ? getAddAiDashboardsUrl(planChooserFrom)
        : getPlanChooserUrl(planChooserFrom),
      "_blank"
    );
  };

  const handleContactSales = async () => {
    track(AnalyticsEventNameType.VibeDashUpgradeModalContactSalesClicked, eventProps);
    setIsSubmitting(true);
    setSubmitError(false);
    const submitFn =
      variant === "expired"
        ? submitWebDashboardExpiredSalesForm
        : submitWebDashboardProactiveSalesForm;
    const ok = await submitFn("Upgrade clicked from web dashboard", clientFlags);
    setIsSubmitting(false);
    if (ok) {
      setIsRequestSent(true);
    } else {
      setSubmitError(true);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/45 p-5"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[380px] overflow-hidden rounded-2xl bg-white shadow-[0_12px_40px_rgba(0,0,0,0.2)]"
        onClick={e => e.stopPropagation()}
      >
        {!isRequestSent ? (
          <>
            <div
              className="flex items-center justify-center py-8"
              style={{ background: "linear-gradient(135deg, #5b2de0 0%, #e8502d 100%)" }}
            >
              <CoefficientIcon fill="white" style={{ width: 60, height: 60 }} />
            </div>
            <button
              onClick={onClose}
              className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-white hover:bg-white/20"
              style={{ background: "rgba(255,255,255,0.2)", border: "none", cursor: "pointer" }}
            >
              <span className="material-icons !text-[16px]">close</span>
            </button>
            <div className="p-6">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-[17px] font-bold text-[#111215]">{copy.title}</span>
                <span className="material-icons !text-[16px] text-[#3053ff]">auto_awesome</span>
              </div>
              <p className="mb-5 text-[13px] leading-[1.6] text-[#444]">{copy.body}</p>
              {submitError && (
                <p className="mb-3 text-[12px] text-red-500">
                  Failed to send. Please try again or contact support.
                </p>
              )}
              {showViewPlans ? (
                <>
                  <button
                    onClick={handleViewPlans}
                    disabled={isOfferLoading}
                    className="w-full rounded bg-[#3053ff] py-3 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-60"
                  >
                    View plans
                  </button>
                  <button
                    onClick={handleContactSales}
                    disabled={isSubmitting}
                    className="mt-2 w-full rounded border border-[#d9d9d9] bg-white py-3 text-[13px] font-semibold text-[#111215] hover:bg-[#f7f7f7] disabled:opacity-60"
                  >
                    {isSubmitting ? "Sending..." : "Contact Sales"}
                  </button>
                </>
              ) : (
                <button
                  onClick={handleContactSales}
                  disabled={isSubmitting}
                  className="w-full rounded bg-[#3053ff] py-3 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-60"
                >
                  {isSubmitting ? "Sending..." : "Contact Sales"}
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="p-6">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="10" fill="#22c55e" />
                  <path
                    d="M6 10.5l3 3 5-6"
                    stroke="white"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-[15px] font-bold text-[#111215]">Request sent</span>
              </div>
              <button
                onClick={onClose}
                className="flex h-6 w-6 items-center justify-center text-[#666] hover:text-[#111]"
                style={{ background: "none", border: "none", cursor: "pointer" }}
              >
                <span className="material-icons !text-[18px]">close</span>
              </button>
            </div>
            <p className="text-[13px] leading-[1.6] text-[#666]">
              Our sales team will reach out with more information about upgrading your plan.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
